// The fonts the web build hands Typst (fonts/, with their licences). The Mac's TypstKit scans the system and
// falls back to Typst's embedded fonts; the browser has neither, so each compile gets the faces its source names:
//
// - Libertinus Serif (OFL): the print interior's face, the same file Typst embeds on the Mac, so print interiors
//   lay out identically; also the last fallback of every prose preset.
// - Courier Prime (OFL): screenplays.
// - Liberation Serif (OFL) stands in for Times New Roman, Liberation Sans (OFL) for Arial and Helvetica (a radio
//   play's face), and TeX Gyre Pagella (GUST Font License) for Palatino: system fonts on the Mac that can't be shipped. Each is metric-compatible with the face it replaces, and
//   answers to its name (TypstKit's font aliases), so the Typst source is the Mac's; the PDF names the stand-in.
import type { FontAliases } from './protocol.ts';

/** Family → its four faces in fonts/. */
export const FONT_FILES: Readonly<Record<string, readonly string[]>> = {
  'Libertinus Serif': ['LibertinusSerif-Regular.otf', 'LibertinusSerif-Italic.otf', 'LibertinusSerif-Bold.otf', 'LibertinusSerif-BoldItalic.otf'],
  'Courier Prime': ['CourierPrime-Regular.ttf', 'CourierPrime-Italic.ttf', 'CourierPrime-Bold.ttf', 'CourierPrime-BoldItalic.ttf'],
  'Liberation Serif': ['LiberationSerif-Regular.ttf', 'LiberationSerif-Italic.ttf', 'LiberationSerif-Bold.ttf', 'LiberationSerif-BoldItalic.ttf'],
  'Liberation Sans': ['LiberationSans-Regular.ttf', 'LiberationSans-Italic.ttf', 'LiberationSans-Bold.ttf', 'LiberationSans-BoldItalic.ttf'],
  'TeX Gyre Pagella': ['TeXGyrePagella-Regular.otf', 'TeXGyrePagella-Italic.otf', 'TeXGyrePagella-Bold.otf', 'TeXGyrePagella-BoldItalic.otf'],
};

/** Families presets name that are set with a bundled stand-in (lower-cased name → bundled family). */
export const STAND_INS: Readonly<Record<string, string>> = {
  'times new roman': 'Liberation Serif',
  times: 'Liberation Serif',
  'liberation serif': 'Liberation Serif',
  arial: 'Liberation Sans',
  helvetica: 'Liberation Sans',
  arimo: 'Liberation Sans',
  'liberation sans': 'Liberation Sans',
  palatino: 'TeX Gyre Pagella',
  'palatino linotype': 'TeX Gyre Pagella',
  'book antiqua': 'TeX Gyre Pagella',
  'tex gyre pagella': 'TeX Gyre Pagella',
  'libertinus serif': 'Libertinus Serif',
  'courier prime': 'Courier Prime',
};

/** Licence files beside the fonts, by family. */
export const FONT_LICENCES: Readonly<Record<string, string>> = {
  'Libertinus Serif': 'LibertinusSerif-LICENSE.txt',
  'Courier Prime': 'CourierPrime-LICENSE.txt',
  'Liberation Serif': 'LiberationSerif-LICENSE.txt',
  'Liberation Sans': 'LiberationSans-LICENSE.txt',
  'TeX Gyre Pagella': 'TeXGyrePagella-LICENSE.txt',
};

export interface FontSelection {
  /** Font files (in fonts/) to load, in order. */
  files: string[];
  /** Bundled family → the names it answers to. */
  aliases: FontAliases;
  /** Families the source names that nothing here sets (Typst falls back to the next in the list). */
  missing: string[];
  /** A stable key for the selection, to skip re-sending the same fonts. */
  key: string;
}

/** The faces for a Typst `font:` list, in the list's order: what `#set text(font: (…))` names. */
export function fontsFor(families: readonly string[]): FontSelection {
  const files: string[] = [];
  const aliases: Record<string, string[]> = {};
  const missing: string[] = [];
  const seen = new Set<string>();
  for (const family of families) {
    const bundled = STAND_INS[family.trim().toLowerCase()];
    if (!bundled) {
      if (!missing.includes(family)) missing.push(family);
      continue;
    }
    if (bundled.toLowerCase() !== family.trim().toLowerCase()) {
      const names = (aliases[bundled] ??= []);
      if (!names.includes(family)) names.push(family);
    }
    if (seen.has(bundled)) continue;
    seen.add(bundled);
    files.push(...FONT_FILES[bundled]);
  }
  return { files, aliases, missing, key: JSON.stringify([files, aliases]) };
}

/** The families a Typst source's first `#set text(font: …)` names, in order (a string or a parenthesised list). */
export function familiesInSource(source: string): string[] {
  const match = /#set text\(font: (\((?:\s*"(?:[^"\\]|\\.)*"\s*,?)*\)|"(?:[^"\\]|\\.)*")/.exec(source);
  if (!match) return [];
  return [...match[1].matchAll(/"((?:[^"\\]|\\.)*)"/g)].map((m) => m[1].replace(/\\(.)/g, '$1'));
}
